import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import useDebounce from '../../hooks/useDebounce';
import { useStyles } from '../styles';
import ETButton from '../../components/ExaminersTreeButton.component';
import { Prompt, SubText } from '../../components/Prompt/Prompt.component';
import {
  ExaminerTreemapTable,
  ShareExaminterTreeMapFunction,
} from './ExaminerTreemapTable/ExaminerTreemapTable.component';
import { PromptDetails, PromptTextRemovedKGZEnum } from '../../models/promptsTypeModels.types';
import {
  deleteExamTaskGroupCoordinator,
  UnassignDomesticCoordinatorKk,
  UnassignExaminingTeamMember,
} from '../../services/exams/examiningTeam/examiningTeam.service';
import { ITaskGroup, DomesticCoordinator } from '../../models/taskGroup.types';
import FilterStateGroup from '../../models/filterStateGroup.types';
import { ExaminerType } from './ExaminerTreemap.types';
import { AssignExaminerDialog } from './Actions/AssignExaminerDialog.component';
import { getExaminersTreemapData } from '../../services/exams/sessions/sessions.service';
import { AxiosError } from 'axios';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { ImportTeams } from './Actions/ImportTeams.component';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import {
  getExamCommitteeId,
  getExamCommitteeNameFromIdString,
} from '../SampleAssessmentRepo/Helpers/AccountTypeRendering';
import { handleTreemapExpansion } from './ExaminerTreemapTable/examinerTreemapSearchUtils';
import { CircularProgress } from '@material-ui/core';

interface ExaminerTreemapPanelProps {
  examinerMode?: boolean;
  sheetId?: string;
  subject?: string;
  assessmentSessionName?: string;
  isExamSheetConfigured?: string;
  isControlProcessActive?: string;
}

const examinatorsFunctions = ['ZWO', 'KGZ', 'PZE', 'Egzaminator', 'Egzaminator-Weryfikator'];

export const ExaminerTreemapPanel: React.FC<ExaminerTreemapPanelProps> = ({
  examinerMode,
  sheetId,
  assessmentSessionName,
  subject,
  isExamSheetConfigured,
  isControlProcessActive,
}) => {
  const examinerTreeMapRef = useRef<ShareExaminterTreeMapFunction | undefined>(undefined);
  const CloseMenuChild = () => {
    if (examinerTreeMapRef && examinerTreeMapRef.current) {
      examinerTreeMapRef.current.CloseMenu();
    }
  };

  const ReloadMenuTreeChild = () => {
    if (examinerTreeMapRef && examinerTreeMapRef.current) {
      examinerTreeMapRef.current.CloseMenu();
    }
  };

  const classes = useStyles();
  const [examinersTreemap, setExaminersTreemap] = useState<ITaskGroup[]>([]);
  const [domesticCoordinator, setDomesticCoordinator] = useState<DomesticCoordinator | undefined>(undefined);
  const [assessmentSessionId, setAssessmentSessionId] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [taskGroups, setTaskGroups] = useState<string[]>([]);
  const [functionsInput, setFunctionsInput] = useState<string>('');
  const [examCommitteeInput, setExamCommitteeInput] = useState<string>('');
  const [taskGroupsInput, setTaskGroupsInput] = useState<string>('');
  const [checkedTaskGroupsMenuItems, setCheckedTaskGroupsMenuItems] = useState<string[]>([]);
  const [checkedFunctionsMenuItems, setCheckedFunctionsMenuItems] = useState<string[]>([]);
  const [checkedExamCommitteeMenuItems, setCheckedExamCommitteeMenuItems] = useState<string[]>([]);
  const [isFirstLoadData, setIsFirstLoadData] = useState<boolean>(true);
  const [isSearchResult, setIsSearchResult] = useState<boolean>(false);
  const [selectedRole] = useState({ role: '', fullName: '' });

  // search state
  const [firstNameSearchInput, setFirstNameSearchInput] = useState<string>('');
  const debouncedFirstNameSearchInput = useDebounce(firstNameSearchInput.length > 1 ? firstNameSearchInput : '', 1000);
  const [lastNameSearchInput, setLastNameSearchInput] = useState<string>('');
  const debouncedLastNameSearchInput = useDebounce(lastNameSearchInput.length > 1 ? lastNameSearchInput : '', 1000);
  const [examinerNumberSearchInput, setExaminerNumberSearchInput] = useState<string>('');
  const debouncedExaminerNumberSearchInput = useDebounce(
    examinerNumberSearchInput.length > 1 ? examinerNumberSearchInput : '',
    1000,
  );

  // filter state
  const [, setfilteredTaskGroups] = useState<ITaskGroup[]>([]);
  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);
  const [isAnyFiltersActive, setIsAnyFiltersActive] = useState<boolean>(false);
  const [isAnyInputActive, setIsAnyInputActive] = useState<boolean>(false);
  const [examinerFunctionId, setExaminerFunctionId] = useState<string>('');
  const [secondaryId, setSecondaryId] = useState<string>('');
  const [assignAssessmentId, setAssignAssessmentId] = useState<string>('');
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [assignModalTitle, setAssignModalTitle] = useState<string>('');
  const [assignModalSubmitType, setAssignModalSubmitType] = useState<ExaminerType>(undefined);
  const [isAssignModalMultiChoice, setIsAssignModalMultiChoice] = useState<boolean>(false);
  const history = useHistory();
  const abortController = useRef<AbortController>();

  useDocumentTitle('Struktura egzaminatorów');

  const setTaskGroupsMenuItems = () => {
    const menuItems: string[] = [];
    examinersTreemap.forEach((item: ITaskGroup) => {
      menuItems.push(item.name);
    });
    setTaskGroups(menuItems);
  };

  useEffect(() => {
    setTaskGroupsInput(checkedTaskGroupsMenuItems.join(';').toLowerCase());
  }, [checkedTaskGroupsMenuItems]);

  useEffect(() => {
    setFunctionsInput(
      checkedFunctionsMenuItems
        .map((item) => (item === 'Egzaminator-Weryfikator' ? 'weryfikator' : item))
        .join(',')
        .toLowerCase(),
    );
  }, [checkedFunctionsMenuItems]);

  useEffect(() => {
    setExamCommitteeInput(checkedExamCommitteeMenuItems.join(',').toLowerCase());
  }, [checkedExamCommitteeMenuItems]);

  useEffect(() => {
    if (examinerTreeMapRef.current?.selectedItemId) {
      setExaminerFunctionId(examinerTreeMapRef.current?.selectedItemId);
    }
  }, [examinerTreeMapRef.current?.selectedItemId]);

  useEffect(() => {
    if (examinerTreeMapRef.current?.selectedSecondaryId) {
      setSecondaryId(examinerTreeMapRef.current?.selectedSecondaryId);
    }
  }, [examinerTreeMapRef.current?.selectedSecondaryId]);

  const getExaminers = useCallback(() => {
    setIsFirstLoadData(true);
    if (abortController && abortController.current) abortController.current.abort();
    abortController.current = new AbortController();
    const { signal } = abortController.current;
    getExaminersTreemapData(
      sheetId ?? '',
      firstNameSearchInput.length > 1 ? firstNameSearchInput : '',
      lastNameSearchInput.length > 1 ? lastNameSearchInput : '',
      examinerNumberSearchInput.length > 1 ? examinerNumberSearchInput : '',
      taskGroupsInput,
      functionsInput,
      examCommitteeInput,
      examinerMode ?? false,
      signal ?? undefined,
    )
      .then((result: ITaskGroup[]) => {
        setExaminersTreemap(result);
        if (result && result.length > 0 && result[0].examSheet) {
          setDomesticCoordinator(result[0].examSheet.domesticCoordinator);
        }
        setIsFirstLoadData(false);
        if (result && result.length > 0 && result[0].assessmentSession) {
          setAssessmentSessionId(result[0].assessmentSession?.assessmentId);
        }
      })
      .catch((e: AxiosError) => {
        setIsFirstLoadData(false);
        if (e?.message !== 'canceled') history.push(getErrorPageUrl(e?.code ?? 500, 'admin'));
      });
  }, [
    examinerMode,
    examinerNumberSearchInput,
    firstNameSearchInput,
    functionsInput,
    history,
    lastNameSearchInput,
    sheetId,
    taskGroupsInput,
    examCommitteeInput,
  ]);

  useEffect(() => {
    if (examinersTreemap.length === 0) return;
    if (
      debouncedFirstNameSearchInput ||
      debouncedLastNameSearchInput ||
      debouncedExaminerNumberSearchInput ||
      taskGroupsInput ||
      functionsInput ||
      examCommitteeInput
    ) {
      setIsAnyInputActive(true);
    } else setIsAnyInputActive(false);
  }, [
    examinersTreemap,
    debouncedFirstNameSearchInput,
    debouncedLastNameSearchInput,
    debouncedExaminerNumberSearchInput,
    taskGroupsInput,
    functionsInput,
    examCommitteeInput,
  ]);

  useEffect(() => {
    if (!examinersTreemap) return;
    if (checkedTaskGroupsMenuItems.length > 0) {
      const filteredItems = examinersTreemap.filter((item) => checkedTaskGroupsMenuItems.includes(item.name));
      setfilteredTaskGroups(filteredItems);
    } else {
      setfilteredTaskGroups(examinersTreemap);
    }
  }, [checkedTaskGroupsMenuItems, examinersTreemap]);

  useEffect(() => {
    if (!examinersTreemap) return;
    const newfilterState: FilterStateGroup[] = [];
    newfilterState.push({ name: 'Grupa zadań:', checkedItems: [] });
    newfilterState.push({ name: 'Funkcja:', checkedItems: [] });
    newfilterState.push({ name: 'Komisja:', checkedItems: [] });

    if (checkedTaskGroupsMenuItems.length > 0) {
      checkedTaskGroupsMenuItems.forEach((item) => {
        newfilterState[0].checkedItems.push(item);
      });
    }
    if (checkedFunctionsMenuItems.length > 0) {
      checkedFunctionsMenuItems.forEach((item) => {
        newfilterState[1].checkedItems.push(item);
      });
    }
    if (checkedExamCommitteeMenuItems.length > 0) {
      checkedExamCommitteeMenuItems.forEach((item) => {
        newfilterState[2].checkedItems.push(getExamCommitteeNameFromIdString(+item));
      });
    }
    setFilterState(newfilterState);
  }, [checkedTaskGroupsMenuItems, checkedFunctionsMenuItems, checkedExamCommitteeMenuItems, examinersTreemap]);

  useEffect(() => {
    if (
      (debouncedFirstNameSearchInput && debouncedFirstNameSearchInput !== firstNameSearchInput) ||
      (debouncedLastNameSearchInput && debouncedLastNameSearchInput !== lastNameSearchInput) ||
      (debouncedExaminerNumberSearchInput && debouncedExaminerNumberSearchInput !== examinerNumberSearchInput) ||
      taskGroupsInput ||
      functionsInput ||
      examCommitteeInput
    ) {
      setIsSearchResult(true);
    } else {
      setIsSearchResult(false);
    }
    getExaminers();
  }, [
    debouncedFirstNameSearchInput,
    debouncedLastNameSearchInput,
    debouncedExaminerNumberSearchInput,
    taskGroupsInput,
    functionsInput,
    examCommitteeInput,
  ]);

  const [anchorElTaskGroup, setAnchorElTaskGroup] = useState<SVGSVGElement | null>(null);
  const [anchorElFunction, setAnchorElFunction] = useState<SVGSVGElement | null>(null);
  const [anchorElExamCommittee, setAnchorElExamCommittee] = useState<SVGSVGElement | null>(null);

  const handleClickTaskGroupMenu = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      if (!anchorElTaskGroup) {
        setAnchorElTaskGroup(event.currentTarget);
      } else setAnchorElTaskGroup(null);
    },
    [anchorElTaskGroup],
  );

  const handleClickFunctionMenu = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      if (!anchorElFunction) {
        setAnchorElFunction(event.currentTarget);
      } else setAnchorElFunction(null);
    },
    [anchorElFunction],
  );

  const handleClickExamCommitteeMenu = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!anchorElExamCommittee) {
      setAnchorElExamCommittee(event.currentTarget);
    } else setAnchorElExamCommittee(null);
  };

  const [isRoleAssignedSuccessPromptVisible, setIsRoleAssignedSuccessPromptVisible] = useState<boolean>(false);
  const [isRoleAlreadyAssignedPromptErrorVisible, setIsRoleAlreadyAssignedPromptErrorVisible] =
    useState<boolean>(false);
  const [isRoleAssignmentPromptErrorVisible, setIsRoleAssignmentPromptErrorVisible] = useState<boolean>(false);
  const [isRoleAssignmentConfirmationPromptVisible, setIsRoleAssignmentConfirmationPromptVisible] =
    useState<boolean>(false);

  const [promptSubTextsZe, setPromptSubTextsZe] = useState<SubText[]>();
  const [isMajorTextKgzHeader, setIsMajorTextKgzHeader] = useState<string>('');
  const [isMajorTextKKHeader, setIsMajorTextKKHeader] = useState<string>('');
  const [isMajorTextZEHeader, setIsMajorTextZEHeader] = useState<string>('');
  const [isKgzExaminatorToRemoved, setIsKgzExaminatorToRemoved] = useState<boolean>(false);
  const [isKKExaminatorToRemovedKK, setIsExaminatorToRemovedKK] = useState<boolean>(false);
  const [isExaminatorToRemovedZE, setExaminatorToRemovedZE] = useState<boolean>(false);

  const [isShowNextPromptZe, setShowNextPromptZe] = useState<boolean>(false);
  const [isShowNextPrompt, setisShowNextPrompt] = useState<boolean>(false);
  const [isShowNextPromptKK, setisShowNextPromptKK] = useState<boolean>(false);
  const [isTextSuccessError, setIsTextSuccessError] = useState<string>('');
  const [isTextSuccessErrorKK, setIsTextSuccessErrorKK] = useState<string>('');

  const [ZeDetails, setZeDetails] = useState<PromptDetails>({
    fullName: '',
    type: 'error',
    examinatorId: '',
    otherText: '',
    groupId: '',
    typePromp: 'ZE',
  });
  const [kgzDetails, setKgzDetails] = useState<PromptDetails>({
    fullName: '',
    type: 'error',
    examinatorId: '',
    otherText: '',
    groupId: '',
    typePromp: 'KGZ',
  });
  const [KKExaminatorDetails, setKKExaminatorDetails] = useState<PromptDetails>({
    fullName: '',
    type: 'error',
    examinatorId: '',
    otherText: '',
    groupId: '',
    typePromp: 'KK',
  });
  const subTextZe = (type: string, name: string) => {
    switch (type) {
      case 'confirmation':
        setPromptSubTextsZe([{ text: name }, { text: 'z Zespołu Egzaminatorów?', isBold: true }]);
        break;
      case 'error':
        setPromptSubTextsZe([
          { text: 'Egzaminatora :' },
          { text: name },
          { text: 'nie powiodło się, spróbuj ponownie później', isBold: true },
        ]);
        break;
      case 'success':
        setPromptSubTextsZe([
          { text: name, isBold: true },
          { text: 'zakończone sukcesem.', isBold: true },
        ]);
        break;
      default:
        setPromptSubTextsZe([{ text: name, isBold: true }]);
        break;
    }
  };
  const showPromptDataHandler = useCallback((promptData: PromptDetails) => {
    switch (promptData.typePromp) {
      case 'ZE':
        if (promptData.examinatorId !== '' && promptData.fullName !== '') {
          promptData.type = 'confirmation';
          setIsMajorTextZEHeader(PromptTextRemovedKGZEnum.GetMajorCofirmationTextZe);
          setZeDetails(promptData);
          setExaminatorToRemovedZE(true);
          CloseMenuChild();
          subTextZe('confirmation', promptData.fullName);
        }
        break;
      case 'KGZ':
        if (promptData.otherText !== '' && promptData.groupId !== '') {
          promptData.type = 'confirmation';
          setIsMajorTextKgzHeader(PromptTextRemovedKGZEnum.GetMajorCofirmationText);
          setKgzDetails(promptData);
          setIsKgzExaminatorToRemoved(true);
          CloseMenuChild();
        }
        break;
    }
  }, []);

  const confirmationActionKgz = () => {
    switch (kgzDetails.type) {
      case 'confirmation':
        setisShowNextPrompt(true);
        break;
      case 'error':
        setisShowNextPrompt(false);
        setIsKgzExaminatorToRemoved(false);
        break;
      case 'success':
        setisShowNextPrompt(false);
        setIsKgzExaminatorToRemoved(false);
        getExaminers();
        break;
      default:
        setisShowNextPrompt(false);
        setIsKgzExaminatorToRemoved(false);
        setIsTextSuccessError('');
        break;
    }
  };

  const cancelActionKgz = () => {
    setisShowNextPrompt(false);
    setIsKgzExaminatorToRemoved(false);
    setKgzDetails({
      fullName: '',
      type: 'confirmation',
      examinatorId: '',
      groupId: '',
      otherText: '',
      typePromp: 'KGZ',
    });
  };

  useEffect(() => {
    if (isShowNextPrompt) {
      deleteExamTaskGroupCoordinator(kgzDetails.groupId)
        .then(
          () =>
            setKgzDetails((prevState) => {
              ReloadMenuTreeChild();
              return { ...prevState, type: 'success' };
            }),
          () =>
            setKgzDetails((prevState) => {
              return { ...prevState, type: 'error' };
            }),
        )
        .catch(() =>
          setKgzDetails((prevState) => {
            return { ...prevState, type: 'error' };
          }),
        );
    }
  }, [isShowNextPrompt]);

  const showPromptDataHandlerKK = (cordinatorUser: PromptDetails) => {
    setKKExaminatorDetails(cordinatorUser);
    setIsMajorTextKKHeader(PromptTextRemovedKGZEnum.GetMajorCofirmationTextKK);
    setIsExaminatorToRemovedKK(true);
  };

  const confirmationActionKK = () => {
    switch (KKExaminatorDetails.type) {
      case 'confirmation':
        setisShowNextPromptKK(true);
        break;
      case 'error':
        setisShowNextPromptKK(false);
        setIsExaminatorToRemovedKK(false);

        break;
      case 'success':
        setisShowNextPromptKK(false);
        setIsExaminatorToRemovedKK(false);
        getExaminers();

        break;
      default:
        setisShowNextPromptKK(false);
        setIsExaminatorToRemovedKK(false);
        setIsTextSuccessErrorKK('');
        break;
    }
  };
  const cancelActionKK = () => {
    setisShowNextPromptKK(false);
    setIsExaminatorToRemovedKK(false);
    setKKExaminatorDetails({
      fullName: '',
      type: 'confirmation',
      examinatorId: '',
      otherText: '',
      groupId: '',
      typePromp: 'KK',
    });
  };

  useEffect(() => {
    if (isShowNextPromptKK) {
      UnassignDomesticCoordinatorKk(KKExaminatorDetails.examinatorId)
        .then(
          () =>
            setKKExaminatorDetails((prevState) => {
              ReloadMenuTreeChild();
              return { ...prevState, type: 'success' };
            }),
          () =>
            setKKExaminatorDetails((prevState) => {
              return { ...prevState, type: 'error' };
            }),
        )
        .catch(() =>
          setKKExaminatorDetails((prevState) => {
            return { ...prevState, type: 'error' };
          }),
        );
    }
  }, [isShowNextPromptKK]);

  useEffect(() => {
    switch (KKExaminatorDetails.type) {
      case 'error':
        setisShowNextPromptKK(false);
        setIsMajorTextKKHeader(PromptTextRemovedKGZEnum.GetSuccessText_2);
        setIsTextSuccessErrorKK(PromptTextRemovedKGZEnum.GetErrorText_2_Bold);
        break;
      case 'success':
        setisShowNextPromptKK(false);

        setIsMajorTextKKHeader(PromptTextRemovedKGZEnum.GetSuccessText_2);
        setIsTextSuccessErrorKK(PromptTextRemovedKGZEnum.GetSuccessText_2_Bold);
        break;
      default:
        setisShowNextPromptKK(false);
        setIsTextSuccessErrorKK('');
        break;
    }
  }, [KKExaminatorDetails.type]);

  useEffect(() => {
    switch (kgzDetails.type) {
      case 'error':
        setisShowNextPrompt(false);
        setIsMajorTextKgzHeader(PromptTextRemovedKGZEnum.GetSuccessText_1);
        setIsTextSuccessError(PromptTextRemovedKGZEnum.GetErrorText_2_Bold);

        break;
      case 'success':
        setisShowNextPrompt(false);

        setIsMajorTextKgzHeader(PromptTextRemovedKGZEnum.GetSuccessText_1);
        setIsTextSuccessError(PromptTextRemovedKGZEnum.GetSuccessText_2_Bold);
        break;
      default:
        setisShowNextPrompt(false);
        setIsTextSuccessError('');
        break;
    }
  }, [kgzDetails.type]);

  const confirmationActionZE = () => {
    switch (ZeDetails.type) {
      case 'confirmation':
        setShowNextPromptZe(true);
        break;
      case 'error':
        setShowNextPromptZe(false);
        setExaminatorToRemovedZE(false);
        break;
      case 'success':
        setShowNextPromptZe(false);
        setExaminatorToRemovedZE(false);
        break;
      default:
        setShowNextPromptZe(false);
        setExaminatorToRemovedZE(false);
        break;
    }
  };
  const cancelActionZe = () => {
    if (examinerTreeMapRef && examinerTreeMapRef.current) {
      examinerTreeMapRef.current.CloseMenu();
    }

    setShowNextPromptZe(false);
    setExaminatorToRemovedZE(false);
    setZeDetails({
      fullName: '',
      type: 'confirmation',
      examinatorId: '',
      otherText: '',
      groupId: '',
      typePromp: 'ZE',
    });
  };

  useEffect(() => {
    switch (ZeDetails.type) {
      case 'error':
        setShowNextPromptZe(false);
        setIsMajorTextZEHeader(PromptTextRemovedKGZEnum.GetTextZeStep2);
        subTextZe(ZeDetails.type, ZeDetails.fullName);
        break;
      case 'success':
        setShowNextPromptZe(false);
        setIsMajorTextZEHeader(PromptTextRemovedKGZEnum.GetTextZeStep2);
        subTextZe(ZeDetails.type, ZeDetails.fullName);
        break;
      default:
        setShowNextPromptZe(false);
        break;
    }
  }, [ZeDetails.type]);

  useEffect(() => {
    if (isShowNextPromptZe) {
      UnassignExaminingTeamMember(ZeDetails.examinatorId)
        .then(
          () =>
            setZeDetails((prevState) => {
              CloseMenuChild();
              examinerTreeMapRef.current?.ReloadTreeMap();
              return { ...prevState, type: 'success' };
            }),
          () =>
            setZeDetails((prevState) => {
              return { ...prevState, type: 'error' };
            }),
        )
        .catch(() =>
          setZeDetails((prevState) => {
            return { ...prevState, type: 'error' };
          }),
        );
    }
  }, [isShowNextPromptZe]);

  const handleTaskGroupFilterChange = useCallback(
    (item: string) => {
      if (checkedTaskGroupsMenuItems.indexOf(item) === -1) {
        setCheckedTaskGroupsMenuItems((arr) => [...arr, item]);
      } else {
        setCheckedTaskGroupsMenuItems(checkedTaskGroupsMenuItems.filter((el: string) => el !== item));
      }
    },
    [checkedTaskGroupsMenuItems],
  );

  const handleFunctionsFilterChange = useCallback(
    (item: string) => {
      if (checkedFunctionsMenuItems.indexOf(item) === -1) {
        setCheckedFunctionsMenuItems((arr) => [...arr, item]);
      } else {
        setCheckedFunctionsMenuItems(checkedFunctionsMenuItems.filter((el: string) => el !== item));
      }
    },
    [checkedFunctionsMenuItems],
  );

  const handleExamCommitteeFilterChange = (item: string) => {
    if (checkedExamCommitteeMenuItems.indexOf(item) === -1) {
      setCheckedExamCommitteeMenuItems((arr) => [...arr, item]);
    } else {
      setCheckedExamCommitteeMenuItems(checkedExamCommitteeMenuItems.filter((el: string) => el !== item));
    }
  };

  const handleCloseFilterItem = (item: string) => {
    if (checkedTaskGroupsMenuItems.indexOf(item) !== -1) {
      setCheckedTaskGroupsMenuItems(checkedTaskGroupsMenuItems.filter((el: string) => el !== item));
    }
    if (checkedFunctionsMenuItems.indexOf(item) !== -1) {
      setCheckedFunctionsMenuItems(checkedFunctionsMenuItems.filter((el: string) => el !== item));
    }
    const examCommitteeFilterItem = getExamCommitteeId(item)[0].toString();
    if (examCommitteeFilterItem && checkedExamCommitteeMenuItems.indexOf(examCommitteeFilterItem) !== -1) {
      setCheckedExamCommitteeMenuItems(
        checkedExamCommitteeMenuItems.filter((el: string) => el !== examCommitteeFilterItem),
      );
    }
  };

  const uncheckAllTaskGroups = useCallback(() => {
    setCheckedTaskGroupsMenuItems([]);
  }, []);

  const uncheckAllFunctions = useCallback(() => {
    setCheckedFunctionsMenuItems([]);
  }, []);

  const uncheckAllExamCommittee = () => {
    setCheckedExamCommitteeMenuItems([]);
  };

  const uncheckAllFilters = () => {
    setCheckedTaskGroupsMenuItems([]);
    setCheckedFunctionsMenuItems([]);
    setCheckedExamCommitteeMenuItems([]);
    setFirstNameSearchInput('');
    setLastNameSearchInput('');
    setExaminerNumberSearchInput('');
  };

  useEffect(() => {
    if (!taskGroups) return;
    if (
      checkedTaskGroupsMenuItems.length > 0 ||
      checkedFunctionsMenuItems.length > 0 ||
      checkedExamCommitteeMenuItems.length > 0
    ) {
      setIsAnyFiltersActive(true);
    } else {
      setIsAnyFiltersActive(false);
    }
  }, [checkedTaskGroupsMenuItems, checkedFunctionsMenuItems, checkedExamCommitteeMenuItems, taskGroups]);

  useEffect(() => {
    if (taskGroups.length > 0) return;
    if (examinersTreemap.length > 0) {
      setTaskGroupsMenuItems();
    }
  }, [taskGroups, examinersTreemap]);

  const handleSearchInput = useCallback((name: string, value: string) => {
    switch (name) {
      case 'firstName':
        setFirstNameSearchInput(value);
        break;
      case 'lastName':
        setLastNameSearchInput(value);
        break;
      case 'examinerNumber':
        setExaminerNumberSearchInput(value);
        break;
    }
  }, []);

  const handleExpandedItems = useCallback(
    (id?: string) => {
      if (id !== undefined && expandedItems.indexOf(id) === -1) {
        setExpandedItems((arr: string[]) => [...arr, id]);
      } else {
        setExpandedItems(expandedItems.filter((item: string) => item !== id));
      }
    },
    [expandedItems],
  );

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setAssignModalTitle('');
    setAssignModalSubmitType(undefined);
    setAssignAssessmentId('');
    getExaminers();
  };

  const handleDomesticCoordinator = () => {
    setAssignModalOpen(true);
    setAssignModalTitle('Przypisz funkcję Koordynatora Krajowego');
    setAssignModalSubmitType('KK');
    setIsAssignModalMultiChoice(false);
  };

  const handleExaminerAssign = useCallback(
    (title: string, type: ExaminerType, isMultiChoice?: boolean, assessmentId?: string) => {
      setAssignModalOpen(true);
      setAssignModalTitle(title);
      setAssignModalSubmitType(type);
      setIsAssignModalMultiChoice(!!isMultiChoice);
      setAssignAssessmentId(assessmentId ?? '');
    },
    [],
  );

  useEffect(() => {
    if (examinersTreemap.length > 0) {
      const isAnyFilter =
        isAnyInputActive ||
        isAnyFiltersActive ||
        checkedFunctionsMenuItems.length > 0 ||
        checkedTaskGroupsMenuItems.length > 0 ||
        checkedExamCommitteeMenuItems.length > 0;
      const isAnyFilterWithoutTaskGroupActive =
        isAnyInputActive || checkedFunctionsMenuItems.length > 0 || checkedExamCommitteeMenuItems.length > 0;
      const newExpandedItems = handleTreemapExpansion(
        examinersTreemap,
        checkedFunctionsMenuItems,
        checkedTaskGroupsMenuItems,
        isAnyFilter,
        isAnyFilterWithoutTaskGroupActive,
      );
      setExpandedItems(newExpandedItems);
    }
  }, [
    checkedTaskGroupsMenuItems,
    examinersTreemap,
    isAnyInputActive,
    isAnyFiltersActive,
    checkedFunctionsMenuItems,
    checkedExamCommitteeMenuItems,
  ]);

  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  const handleOnScroll = () => {
    setIsScrolled((state) => !state);
  };

  return (
    <div style={{ backgroundColor: '#F7F7FA' }}>
      <div
        style={{ top: '45px' }}
        className={`${classes.filtersContainer} ${examinerMode ? classes.filtersContainerWithoutTopMargin : ''}`}
      >
        <Typography variant="h2" className={classes.gridHeader}>
          {subject} ({assessmentSessionName})
        </Typography>
        <div style={{ display: 'flex', padding: '20px' }}>
          {isSearchResult && <Typography className={classes.searchResult}></Typography>}
          {(usePermission(UserPermission.AdminExaminersLineupsRead) || examinerMode) && examinersTreemap.length > 0 && (
            <ETButton
              domesticCoordinatorId={
                domesticCoordinator?.domesticCoordinatorId ? domesticCoordinator?.domesticCoordinatorId : undefined
              }
              id={domesticCoordinator?.examiner ? domesticCoordinator?.examiner?.examinerId : undefined}
              name={
                domesticCoordinator?.examiner
                  ? domesticCoordinator.examiner.firstName + ' ' + domesticCoordinator.examiner.lastName
                  : undefined
              }
              onClick={showPromptDataHandlerKK}
              handleDomesticCoordinator={handleDomesticCoordinator}
              isRemovingHidden={examinerMode}
            />
          )}
          {usePermission(UserPermission.AdminOkeTeamImport) && (
            <ImportTeams
              sheetId={sheetId ?? ''}
              examTaskGroupIds={examinersTreemap.map((item) => item.taskGroupId)}
              onSuccess={() => getExaminers()}
              assessmentSessionName={assessmentSessionName}
              isExamSheetConfigured={isExamSheetConfigured === 'true' ? true : false}
            />
          )}
        </div>
      </div>
      <div
        className={`${classes.tableContainer} ${examinerMode ? classes.tableContainerWithoutTopMargin : ''}`}
        onScroll={handleOnScroll}
        style={{ top: '125px', maxHeight: 'calc(100% - 165px)' }}
      >
        {isFirstLoadData && (
          <div style={{ padding: '20px', color: 'gray' }}>
            <CircularProgress size={40} style={{ marginTop: '10px' }} />
          </div>
        )}
        <ExaminerTreemapTable
          ref={examinerTreeMapRef}
          data={examinersTreemap}
          examinerMode={examinerMode}
          expandedItems={expandedItems}
          handleExpandedItems={handleExpandedItems}
          filterTaskGroupsMenuItems={taskGroups}
          filterFunctionsMenuItems={examinatorsFunctions}
          handleSearchInput={handleSearchInput}
          firstNameSearchInput={firstNameSearchInput}
          lastNameSearchInput={lastNameSearchInput}
          examinerNumberSearchInput={examinerNumberSearchInput}
          reload={getExaminers}
          handleClickTaskGroupMenu={handleClickTaskGroupMenu}
          handleClickFunctionMenu={handleClickFunctionMenu}
          handleClickExamCommitteeMenu={handleClickExamCommitteeMenu}
          checkedTaskGroupsMenuItems={checkedTaskGroupsMenuItems}
          checkedFunctionsMenuItems={checkedFunctionsMenuItems}
          checkedExamCommitteeMenuItems={checkedExamCommitteeMenuItems}
          setCheckedTaskGroupsMenuItems={setCheckedTaskGroupsMenuItems}
          setCheckedFunctionsMenuItems={setCheckedFunctionsMenuItems}
          handleTaskGroupFilterChange={handleTaskGroupFilterChange}
          handleFunctionsFilterChange={handleFunctionsFilterChange}
          handleExamCommitteeFilterChange={handleExamCommitteeFilterChange}
          uncheckAllTaskGroups={uncheckAllTaskGroups}
          uncheckAllFunctions={uncheckAllFunctions}
          uncheckAllExamCommittee={uncheckAllExamCommittee}
          uncheckAllFilters={uncheckAllFilters}
          isAnyFiltersActive={isAnyFiltersActive}
          filterState={filterState}
          isAnyInputActive={isAnyInputActive}
          handleCloseFilterItem={handleCloseFilterItem}
          onModalFunctionAction={showPromptDataHandler}
          openAssignModal={handleExaminerAssign}
          isLoader={isFirstLoadData}
          isTableScrolled={isScrolled}
          isControlProcessActive={isControlProcessActive === 'true' ? true : false}
          assessmentSessionId={assessmentSessionId}
          assessmentSessionName={assessmentSessionName ?? ''}
        />
        <Prompt
          type={ZeDetails.type}
          isBold={true}
          open={isExaminatorToRemovedZE}
          mainText={isMajorTextZEHeader}
          subTexts={promptSubTextsZe}
          onConfirm={() => confirmationActionZE()}
          onCancel={ZeDetails.type === 'confirmation' ? cancelActionZe : undefined}
          mainActionButtonText={ZeDetails.type === 'confirmation' ? 'TAK' : 'POWRÓT DO LISTY'}
          cancelButtonText="Anuluj"
        />
        <Prompt
          type={KKExaminatorDetails.type}
          isBold={true}
          open={isKKExaminatorToRemovedKK}
          mainText={isMajorTextKKHeader}
          subTexts={[{ text: KKExaminatorDetails.fullName }, { text: isTextSuccessErrorKK, isBold: true }]}
          onConfirm={() => confirmationActionKK()}
          onCancel={KKExaminatorDetails.type === 'confirmation' ? cancelActionKK : undefined}
          mainActionButtonText={KKExaminatorDetails.type === 'confirmation' ? 'TAK' : 'POWRÓT DO LISTY'}
          cancelButtonText="Anuluj"
        />

        <Prompt
          type={kgzDetails.type}
          isBold={true}
          open={isKgzExaminatorToRemoved}
          mainText={isMajorTextKgzHeader}
          subTexts={[{ text: kgzDetails.otherText }, { text: isTextSuccessError, isBold: true }]}
          onConfirm={() => confirmationActionKgz()}
          onCancel={kgzDetails.type === 'confirmation' ? cancelActionKgz : undefined}
          mainActionButtonText={kgzDetails.type === 'confirmation' ? 'TAK' : 'POWRÓT DO LISTY'}
          cancelButtonText="Anuluj"
        />
        <Prompt
          type="success"
          open={isRoleAssignedSuccessPromptVisible}
          isBold={true}
          mainText={`Przypisanie funkcji ${selectedRole.role} Egzaminatorowi:`}
          subTexts={[{ text: selectedRole.fullName }, { text: 'zakończone sukcesem.', isBold: true }]}
          onConfirm={() => setIsRoleAssignedSuccessPromptVisible(false)}
          mainActionButtonText="POWRÓT DO LISTY"
        />

        <Prompt
          type="error"
          open={isRoleAlreadyAssignedPromptErrorVisible}
          isBold={true}
          mainText={`Przypisanie Egzaminatorowi funkcji ${selectedRole.role} nie powiodło się.`}
          subTexts={[
            { text: selectedRole.fullName },
            {
              text: 'pełni już funkcję KGZ/PZE/E w danej sesji oceniania.',
              isBold: true,
            },
          ]}
          onConfirm={() => setIsRoleAlreadyAssignedPromptErrorVisible(false)}
          mainActionButtonText="POWRÓT DO LISTY"
        />
        <Prompt
          type="error"
          isBold={true}
          open={isRoleAssignmentPromptErrorVisible}
          mainText={`Przypisanie funkcji ${selectedRole.role} Egzaminatorowi:`}
          subTexts={[
            { text: selectedRole.fullName },
            {
              text: 'nie powiodło się, spróbuj ponownie później.',
              isBold: true,
            },
          ]}
          onConfirm={() => setIsRoleAssignmentPromptErrorVisible(false)}
          mainActionButtonText="POWRÓT DO LISTY"
        />

        <Prompt
          type="confirmation"
          isBold={true}
          open={isRoleAssignmentConfirmationPromptVisible}
          mainText={`Czy na pewno chcesz usunąć przypisaną funkcję ${selectedRole.role} Egzaminatorowi:`}
          subTexts={[{ text: selectedRole.fullName }]}
          mainActionButtonText="TAK"
          onConfirm={() => setIsRoleAssignmentConfirmationPromptVisible(false)}
          onCancel={() => setIsRoleAssignmentConfirmationPromptVisible(false)}
        />

        <AssignExaminerDialog
          id={'assignExaminerDialog'}
          title={assignModalTitle}
          isOpen={assignModalOpen}
          type={assignModalSubmitType}
          isMultiChoice={isAssignModalMultiChoice}
          onClose={handleCloseAssignModal}
          examinerFunctionId={examinerFunctionId}
          domesticCoordinatorId={domesticCoordinator?.domesticCoordinatorId}
          secondaryId={secondaryId}
          assessmentId={assignAssessmentId}
        />
      </div>
    </div>
  );
};
